/** @jsxImportSource @emotion/react */

import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Dropdown, DropdownItemProps, DropdownProps, Input } from 'semantic-ui-react';
import _ from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import { useAppDispatch } from '../../../core/store';
import { DistributionListGroup, DistributionListGroupCreationRequest, DistributionListGroupUpdateRequest } from '../models/distributionListGroup';
import { selectKeringRegions, selectIsFetching, fetchKeringRegions } from '../../users/store/keringRegionsSlice';
import { clearKeringCountries, fetchKeringCountries, selectIsFetchingKeringCountries, selectKeringCountries } from '../../users/store/keringCountriesSlice';
import { createDistributionListGroup, updateDistributionListGroup, isGroupNameUnique, selectDistributionListGroups } from '../store/distributionListGroupSlice';
import { AdUser, Contact, fromAdUserToContact } from '../../users/model/user.model';
import { brandCodeList, toBrand } from '../../../shared/model/brand.model';
import { Region } from '../../../shared/model/region.model';
import { Country } from '../../../shared/model/country.model';
import { useCtrlEnterToRunCallback, useEscToRunCallback } from '../../../core/utils';
import { SimpleField } from '../../../shared/SimpleField';
import style from './distributionListGroupCard.style';
import userApi from '../../users/utils/api';


export interface DistributionListGroupCardWritableProps {
  group: DistributionListGroup,
  onCancel: () => void;
}


export const DistributionListGroupCardWritable = ({ group, onCancel }: DistributionListGroupCardWritableProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const groups = useSelector(selectDistributionListGroups);
  const [distributionList, setDistributionList] = useState<Contact[]>(group?.distributionList ?? []);
  const [groupName, setGroupName] = useState<string>(group?.name ?? '');
  const [brandCode, setBrandCode] = useState<string>(group?.brand.code ?? '');
  const [region, setRegion] = useState<Region | null>(group?.region ?? null);
  const [countries, setCountries] = useState<Country[]>(group?.countries ?? []);
  const [showErrors, setShowErrors] = useState(false);

  const keringRegions = useSelector(selectKeringRegions);
  const keringCountries = useSelector(selectKeringCountries);
  const isFetchingKeringCountries = useSelector(selectIsFetchingKeringCountries);
  const isFetchingKeringRegions = useSelector(selectIsFetching);

  const isEditMode = group.id !== '';

  const isBrandSelected = useMemo(
    () => brandCode !== '',
    [brandCode]
  )

  const isGroupNameFilled = useMemo(
    () => groupName !== '',
    [groupName]
  )

  const isRegionSelected = useMemo(
    () => region != null && region.description !== '',
    [region]
  )

  const isNameUnique = useCallback(
    () => isGroupNameUnique(groupName, brandCode, groups, group.id ?? null),
    [brandCode, group.id, groupName, groups]
  )

  const isDistributionListGroupFilled = useMemo(
    () => distributionList.length !== 0,
    [distributionList]
  )

  const brandErrorMsg = useMemo((): string => {
    if (!isBrandSelected) {
      return t('distributionListGroups.brandRequired');
    }
    return '';
  }, [isBrandSelected, t]);

  const regionErrorMsg = useMemo((): string => {
    if (!isRegionSelected) {
      return t('distributionListGroups.regionRequired');
    }
    return '';
  }, [isRegionSelected, t]);

  const groupNameNotFilledErrorMessage = useMemo((): string => {
    if (!isGroupNameFilled) {
      return t('distributionListGroups.groupNameRequired');
    }
    return '';
  }, [isGroupNameFilled, t]);

  const groupNameNotUniqueErrorMessage = useMemo((): string => {
    if (!isNameUnique()) {
      return t('distributionListGroups.groupNameNotUnique');
    }
    return '';
  }, [isNameUnique, t]);

  
  const distributionListGroupNotFilledErrorMessage = useMemo((): string => {
    if (!isDistributionListGroupFilled) {
      return t('distributionListGroups.groupRequired');
    }
    return '';
  }, [isDistributionListGroupFilled, t]);


  const upsertDistributionList = async (): Promise<void> => {
    if (!region) {
      return;
    }
    const request: DistributionListGroupCreationRequest | DistributionListGroupUpdateRequest = {
      name: groupName,
      brand: toBrand(brandCode),
      distributionList,
      region,
      countries,
    };
    if (isEditMode) {
      await dispatch(updateDistributionListGroup(group!.id, request));
    } else {
      await dispatch(createDistributionListGroup(request));
    }
  };

  const resetStatus = () => {
    onCancel();
    setGroupName(group?.name ?? '');
    setBrandCode(group?.brand.code ?? '');
    setDistributionList(group?.distributionList ?? []);
    setRegion(group?.region ?? null);
    setCountries(group?.countries ?? []);
  };

  const handleGroupNameTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setGroupName(value);
  };

  const handleBrandSelection = (value: string) => {
    setBrandCode(value);
  };


  const handleRegionChange = (value: string) => {
    const currentRegion = keringRegions.find(r => r.description === value) ?? null;
    setRegion(currentRegion);
    setCountries([]);
  };
  const handleCountriesChange = (values: Country[]) => {
    const selectedCodes = values.map(v => v.code);
    setCountries(keringCountries?.filter(c => selectedCodes.includes(c.code)));
  };

  const handleSave = () => {
      setShowErrors(true)
      if (isBrandSelected && isGroupNameFilled && isNameUnique() && isDistributionListGroupFilled && isRegionSelected) {
        upsertDistributionList().then(() => {
        resetStatus();
      });
      }
  };

  useCtrlEnterToRunCallback(handleSave);
  useEscToRunCallback(onCancel);

  useEffect(() => {
    dispatch(fetchKeringRegions());
  }, [dispatch]);

  useEffect(() => {
    if (region == null) {
      dispatch(clearKeringCountries());
    } else {
      dispatch(fetchKeringCountries([region.description]));
    }
  }, [dispatch, region]);


  return (
    <div css={style.card}>
        <div css={style.info}>
          <SimpleField
              error={showErrors && (!isGroupNameFilled || !isNameUnique())}
              errorMessage={[groupNameNotFilledErrorMessage, groupNameNotUniqueErrorMessage]}
              title={t('distributionListGroups.distributionListGroupName')}
              icon='id badge'>
          <Input
            css={style.inputField}
            value={groupName}
            type='text'
            onChange={handleGroupNameTextChange}
            placeholder={t('distributionListGroups.writeName')}
          />
          </SimpleField>
          <SimpleField
              css={[style.brandField]}
              error={showErrors && !isBrandSelected}
              errorMessage={brandErrorMsg}
              title={t('distributionListGroups.brand')}
              icon='copyright'>
          <DropDownBrands error={showErrors && !isBrandSelected} selectedBrand={brandCode} onChange={handleBrandSelection} />
          </SimpleField>
        </div>
        <div css={style.info}>
            <SimpleField
              title={t('distributionListGroups.distributionListGroup')}
              error={showErrors && !isDistributionListGroupFilled}
              errorMessage={distributionListGroupNotFilledErrorMessage}
              icon='mail'>
            <DropDownDistributionListGroups
               onSelection={(contacts: Contact[]) => setDistributionList(contacts)}
               contacts={distributionList}
            />
            </SimpleField>
        </div>
        <div css={style.info}>
          <SimpleField 
            error={showErrors && !isRegionSelected}
            errorMessage={regionErrorMsg}
            title={t('distributionListGroups.region')} 
            icon='globe'
            >
              <DropDownRegions
                selectedRegion={region?.description ?? ''}
                onChange={handleRegionChange}
                keringRegionList={keringRegions}
                loading={isFetchingKeringRegions}
                error={showErrors && !isRegionSelected}
              />
          </SimpleField>
          <SimpleField title={t('distributionListGroups.countries')} icon='globe'>
            <DropDownCountries
              selectedCountries={countries}
              onChange={handleCountriesChange}
              keringCountryList={keringCountries}
              disabled={region == null}
              loading={isFetchingKeringCountries}
            />
          </SimpleField>
        </div>
        <div css={style.buttonsContainer}>
        <Button content={t('distributionListGroups.cancel')} icon='times' onClick={onCancel} />
        <Button css={style.saveButton} content={t('distributionListGroups.save')} icon='save' onClick={() => handleSave()} />
      </div>
    </div>
  );
};

interface DropdownRegionsProps {
  selectedRegion: string;
  onChange: (region: string) => void;
  keringRegionList: Region[];
  loading: boolean;
  error: boolean;
}

const DropDownRegions = ({
  selectedRegion,
  onChange,
  keringRegionList,
  loading,
  error
}: DropdownRegionsProps): JSX.Element => {
  const { t } = useTranslation();
  
  return (
    <Dropdown
      compact
      loading={loading}
      css={style.dropDownField}
      placeholder={t('distributionListGroups.region') as string}
      value={selectedRegion}
      selection
      error={error}
      fluid
      clearable
      disabled={loading}
      options={Object.values(keringRegionList).map(r => ({ key: r.code, text: r.description, value: r.description }))}
      onChange={(event, data: DropdownProps) => {
        const value = data.value as string;
        onChange(value);
      }}
    />
  );
};


interface DropdownCountriesProps {
  selectedCountries: Country[];
  onChange: (countries: Country[]) => void;
  keringCountryList: Country[];
  disabled: boolean;
  loading: boolean;
}

const DropDownCountries = ({
  selectedCountries,
  onChange,
  keringCountryList,
  disabled,
  loading,
}: DropdownCountriesProps): JSX.Element => {
  const { t } = useTranslation();
  const allCountriesDescription = t('distributionListGroups.allCountriesDescription');

  return (
    <Dropdown
      loading={loading}
      compact
      css={style.customDropDownField}
      placeholder={allCountriesDescription || ''}
      value={selectedCountries.map(country => country.code)}
      selection
      multiple
      search
      disabled={disabled || loading}
      fluid
      clearable
      options={Object.values(keringCountryList).map(country => ({
        key: country.code,
        text: country.description,
        value: country.code,
      }))}
      onChange={(event, data: DropdownProps) => {
        const values = data.value as string[];
        onChange(keringCountryList.filter(country => values.includes(country.code)));
      }}
    />
  );
};

interface DropdownBrandsProps {
  selectedBrand: string;
  onChange: (brand: string) => void;
  error: boolean;
}

const DropDownBrands = ({ error, selectedBrand, onChange }: DropdownBrandsProps): JSX.Element => {
  const { t } = useTranslation();
  const options = useMemo(() => {
    const brandOptions = brandCodeList.map(brandCode => {
      const brand = toBrand(brandCode);
      return { key: brand.code, text: brand.description, value: brand.code };
    });
    return brandOptions
  }, []);

  return (
    <Dropdown
      compact
      css={style.dropDownField}
      placeholder={t('distributionListGroups.brand') as string}
      value={selectedBrand}
      selection
      error={error}
      fluid
      clearable
      options={options}
      onChange={(event, data: DropdownProps) => {
        const value = data.value != null ? data.value as string : '';
        onChange(value);
      }}
    />
  );
};


interface DropdownDistributionListGroupsProps {
  onSelection: (contacts: Contact[]) => void;
  contacts: Contact[];
}

const DropDownDistributionListGroups = ({ onSelection, contacts }: DropdownDistributionListGroupsProps): JSX.Element => {
  const { t } = useTranslation();
  const [adUsers, setAdUsers] = useState<AdUser[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>(contacts);
  const [isFetching, setIsFetching] = useState(false);
  const getOptions = useCallback(() => {
    const generateItem = (user: AdUser): DropdownItemProps => {
      const fullName = `${user.firstName} ${user.familyName}`;
      const text = 'email'
      return {
        key: user.email,
        value: user.email,
        text: `${text as string === 'fullName' ? fullName : user.email}`,
        content: (
          <div>
            <div css={style.dropdownItemName}>{fullName}</div>
            <div>{user.email}</div>
          </div>
        ),
      };
    };

    const mergedList = _.uniqBy(
      adUsers.concat(
        selectedContacts.map(u => ({ email: u.email.toLowerCase(), firstName: '', familyName: '', validBrands: [] }))
      ),
      'email'
    );

    return mergedList.map(generateItem);
  }, [adUsers, selectedContacts]);

  const searchAdUsers = useDebouncedCallback(async (userToSearch: string) => {
    if (userToSearch.trim() === '') {
      setAdUsers([]);
      return;
    }
    if (userToSearch.trim().length < 3) {
      return;
    }
    setIsFetching(true);
    try {
      const result = await userApi.getAdUsers(userToSearch);
      if (result.length > 0) {
        setAdUsers(result);
      }
    } catch {
      setAdUsers([]);
    } finally {
      setIsFetching(false);
    }
  }, 300);

  useEffect(() => {
    if (!contacts.length ) {
      setSelectedContacts([]);
    }
  }, [contacts]);

  return (
    <Dropdown
      css={style.customDropDownField}
      placeholder={t('distributionListGroups.search') as string}
      value={_.sortBy(selectedContacts, 'email').map(u => u.email)}
      selection
      multiple
      clearable
      noResultsMessage={t('noResultsFound')}
      search={() => {
        const options = getOptions().filter(
          o => !selectedContacts.map(c => c.email).includes(o.text as string)
        );
        return options;
      }}
      options={getOptions()}
      onChange={(e, { value }) => {
        const searchedUsers = adUsers.filter(u => (value as string[]).includes(u.email)).map(u => fromAdUserToContact(u));
        const selectedUsers = selectedContacts.filter(c => (value as string[]).includes(c.email));
        const mergedList = _.uniqBy(searchedUsers.concat(selectedUsers), 'email');
        onSelection(mergedList);
        setSelectedContacts(mergedList);
      }}
      onSearchChange={(e, { searchQuery }) => {
        searchAdUsers(searchQuery);
        if (searchQuery.trim() === '') {
          onSelection([]);
        }
      }}
      selectOnBlur={false}
      disabled={isFetching}
      loading={isFetching}
    />
  );
};

